import type { Placement } from '@popperjs/core';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import {
  DesignSystemContextProvider,
  usePortalContainer,
} from '../DesignSystemContext';
import { useClickOutside } from '../useClickOutside';

export type { Placement };

export interface PopperProps extends React.HTMLAttributes<HTMLDivElement> {
  content: React.ReactNode;
  isOpen: boolean;
  placement?: Placement | undefined;
  fallbackPlacements?: Placement[];
  onClose: () => void;
  offset?: number;
  skidding?: number;
  isDark?: boolean;
  // if true, click outside will _only_ close this popper,
  // and not propagate through to the element which was clicked
  preventClickOutsidePropagation?: boolean;
  /**
   * Disables popper.js's `hide` modifier. By default the popper auto-hides
   * (becomes invisible) whenever its reference element is considered clipped
   * by a scrolling ancestor. That detection misfires for references anchored
   * in a `position: fixed` bar that is a DOM descendant of an `overflow`
   * container (e.g. the redesign bottom nav bar on the Home page): the
   * reference is always visible on screen, but popper.js flags it as
   * `reference-hidden` and the content never appears. Set this for poppers
   * whose anchor is always on-screen so the content can't be falsely hidden.
   */
  disableAutoHide?: boolean;
}

/** Override popperjs fallbacks for some placements, so longer dropdowns get more space to fit on the page */
const FALLBACK_PLACEMENTS: { [placement in Placement]?: Placement[] } = {
  'bottom-end': ['top-end', 'left-start', 'right-start'],
  'bottom-start': ['top-start', 'right-start', 'left-start'],
  bottom: ['top', 'right-start', 'left-start'],
  'top-end': ['bottom-end', 'left-end', 'right-end'],
  'top-start': ['bottom-start', 'right-end', 'left-end'],
  top: ['bottom', 'right-end', 'left-end'],
};

/**
 * A popper component for complex floating elements.
 *
 * Consider using SharedPopper instead. It is more memory-efficient.
 */
export function Popper({
  children,
  content,
  isOpen,
  placement = 'bottom',
  fallbackPlacements = FALLBACK_PLACEMENTS[placement],
  onClose,
  onClick,
  className,
  offset = 4,
  skidding = 0,
  isDark,
  preventClickOutsidePropagation,
  disableAutoHide,
  ...rest
}: PopperProps) {
  const parentContainerElement = usePortalContainer();

  // The container holds this popup _plus any descendant popups_ (e.g. sub menus).
  // Descendants access this container through `DesignSystemContext`.
  // This lets us use the DOM structure for the clickOutside hook to work as expected.
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);

  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);

  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  useClickOutside(
    [containerElement, referenceElement],
    isOpen
      ? (event) => {
          if (containerElement && preventClickOutsidePropagation) {
            event.stopPropagation();
            event.stopImmediatePropagation();
            containerElement.setPointerCapture(event.pointerId);
          }

          onClose();
        }
      : undefined,
  );

  const {
    styles: popperStyles,
    attributes,
    forceUpdate,
  } = usePopper(referenceElement, popperElement, {
    placement,
    strategy:
      'fixed' /* avoids dropdown triggering overflow on document.body */,
    modifiers: [
      { name: 'flip', options: { fallbackPlacements } },
      { name: 'offset', options: { offset: [skidding, offset] } },
      { name: 'preventOverflow', options: { padding: 10 } },
      { name: 'eventListeners', enabled: isOpen },
      ...(disableAutoHide ? [{ name: 'hide', enabled: false }] : []),
    ],
  });

  useEffect(() => {
    if (content && forceUpdate) {
      queueMicrotask(() => {
        forceUpdate();
      });
    }
  }, [content, forceUpdate]);

  // Until popper has actually placed the panel, react-popper reports its
  // initial style — `{ position: fixed, top: 0, left: 0 }` at the viewport
  // corner with no transform. Painting the panel in that frame makes it
  // visibly jump from the corner to its anchored spot as popper's transform
  // lands (most noticeable as a horizontal slide with `-end` placements).
  // Popper sets `data-popper-placement` once it has computed a placement, so
  // gate visibility on that: the panel fades in already positioned.
  const isPositioned = attributes.popper?.['data-popper-placement'] != null;

  // close the popper if user clicks on an enabled button inside it
  // (you can override this by adding `event.stopPropagation()` in the button click handler)
  //
  // Role/disabled are read via getAttribute rather than the `element.role` /
  // `element.ariaDisabled` IDL reflections: those ARIAMixin properties aren't
  // supported on older WebKit (iPad Safari < 17), where they return undefined
  // even when the attribute is set. Since menu items render as
  // `<li role="button">` (not real <button>s), the IDL check failed to close
  // the menu on those devices — the attribute read works everywhere.
  const handleClickPopper: React.MouseEventHandler = (event) => {
    let target: EventTarget | null = event.target; // eslint-disable-line prefer-destructuring

    while (target) {
      if (target instanceof HTMLButtonElement) {
        if (!target.disabled) onClose();
        target = null;
      } else if (
        target instanceof Element &&
        target.getAttribute('role') === 'button'
      ) {
        if (target.getAttribute('aria-disabled') !== 'true') onClose();
        target = null;
      } else if (target instanceof Element) {
        target = target.parentElement;
      } else {
        target = null;
      }
    }
  };

  /* eslint-disable jsx-a11y/no-static-element-interactions */

  return (
    <>
      <div
        role={onClick ? 'button' : undefined}
        aria-pressed={onClick ? isOpen : undefined}
        {...rest}
        className={cx(className, 'aria-pressed:tw-opacity-40')}
        onClick={onClick}
        ref={setReferenceElement}
      >
        {children}
      </div>

      {createPortal(
        <div
          ref={setContainerElement}
          className={cx('tw-contents', isDark && 'tw-dark')}
        >
          <DesignSystemContextProvider portalContainer={containerElement}>
            <div
              ref={setPopperElement}
              className={cx(
                // override browser's [hidden] rule so content fades out
                '[&[hidden]]:tw-block',
                '[&[hidden]]:tw-invisible',
                '[&[hidden]]:tw-opacity-0',
                'data-[popper-reference-hidden=true]:tw-invisible',
                'data-[popper-reference-hidden=true]:tw-opacity-0',
                // Stay hidden until popper has positioned the panel, so it
                // fades in at its anchored spot instead of jumping from (0,0).
                !isPositioned && 'tw-invisible tw-opacity-0',
                'tw-transition-opacity',
                'tw-z-40',
              )}
              onClick={handleClickPopper}
              style={popperStyles.popper}
              {...attributes.popper}
              hidden={!isOpen}
            >
              {content}
            </div>
          </DesignSystemContextProvider>
        </div>,
        parentContainerElement,
      )}
    </>
  );
}
